import classnames from 'classnames';
import type * as React from 'react';
import { useRef } from 'react';

import Tooltip from '../Tooltip';

import './FilterSearchInput.css';

export interface FilterSearchInputProps {
  /**
   * Callback fired when the search input value changes.
   * Receives the raw input value immediately on every keystroke.
   */
  onSearchChange: (query: string) => void;

  /**
   * The current value of the search input.
   * Typically the immediate `searchInput` state from the parent.
   */
  searchInput: string;

  /**
   * Placeholder text displayed when the input is empty.
   * @default 'Search...'
   */
  placeholder?: string;

  /**
   * Visually hidden label for screen readers.
   */
  label?: string;

  /**
   * Optional CSS class applied to the wrapper element.
   */
  className?: string;

  /**
   * Whether the search input is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Data test ID for testing.
   * @default 'filter-search-input'
   */
  'data-testid'?: string;
}

/**
 * FilterSearchInput - Controlled search input for table filtering
 *
 * Renders a search input with a magnifying glass icon and an optional
 * clear button that appears once the user has typed enough characters.
 * The clear button resets the input and refocuses it.
 */
function FilterSearchInput({
  onSearchChange,
  searchInput,
  placeholder = 'Search...',
  label,
  className,
  disabled = false,
  'data-testid': dataTestId,
}: FilterSearchInputProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const baseTestId = dataTestId ?? 'filter-search-input';
  const inputId = `${baseTestId}-input`;
  const searchClasses = classnames('filter-search__search', className);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.target.value);
  };

  const handleClearSearch = () => {
    onSearchChange('');
    searchInputRef.current?.focus();
  };

  return (
    <div
      className={searchClasses}
      data-testid={baseTestId}>
      {label && (
        <label
          className='filter-search__label'
          data-testid={`${baseTestId}-label`}
          htmlFor={inputId}>
          {label}
        </label>
      )}
      <svg
        aria-hidden='true'
        className='filter-search__search-icon'
        fill='none'
        focusable='false'
        stroke='currentColor'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        xmlns='http://www.w3.org/2000/svg'>
        <circle
          cx='11'
          cy='11'
          r='8'
        />
        <path d='m21 21-4.35-4.35' />
      </svg>
      <input
        ref={searchInputRef}
        aria-label='Search'
        className='filter-search__input'
        data-testid={`${baseTestId}-search-input`}
        disabled={disabled}
        id={inputId}
        name='filter-search'
        placeholder={placeholder}
        type='search'
        value={searchInput}
        onChange={handleSearchChange}
      />
      {searchInput.length > 0 && (
        <Tooltip
          hasInteractiveChild
          className='filter-search__clear-tooltip'
          data-testid={`${baseTestId}-clear-tooltip`}
          placement='top'
          title='Clear Search'>
          <button
            aria-label='Clear search'
            className='filter-search__clear-button'
            data-testid={`${baseTestId}-clear`}
            disabled={disabled}
            type='button'
            onClick={handleClearSearch}>
            <svg
              aria-hidden='true'
              fill='currentColor'
              focusable='false'
              height='18'
              viewBox='0 0 24 24'
              width='18'
              xmlns='http://www.w3.org/2000/svg'>
              <path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
            </svg>
          </button>
        </Tooltip>
      )}
    </div>
  );
}

export default FilterSearchInput;
